<script setup lang="ts">
import {
  URL_ADDRESS_PAGE,
  URL_OFFER_CREATE,
  URL_SEARCH,
  URL_USER_DEALS,
  URL_USER_INBOX,
  URL_USER_NOTIFICATIONS,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_MANAGE,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROFILE_MANAGE,
  URL_USER_PROPOSAL,
} from '@tools/routes/routes';
import IconHeartStraight from '@icons/ph_heart-straight.svg';
import IconMagnifyingGlass from '@icons/ph_magnifying-glass.svg';
import { trans } from '@elements/translation.ts';
import PlusMenuLink from '@layout/nav-v2/mobile/components/PlusMenuLink.vue';
import Icon from '@components/Icon.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { storeToRefs } from 'pinia';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconAlignTop from '@icons/ph_align-top.svg';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import IconBuildings from '@icons/ph_buildings.svg';
import PlusMenuItemGroup from '@layout/nav-v2/mobile/components/PlusMenuItemGroup.vue';
import IconBriefCase from '@icons/ph_briefcase.svg';
import Divider from '@components/Divider.vue';
import IconUserCircle from '@icons/ph_user-circle.svg';
import IconBellRinging from '@icons/ph_bell-ringing.svg';
import IconPlus from '@icons/ph_plus.svg';

const navbarStore = useNavbarStore();
const { notifications } = storeToRefs(navbarStore);
const { setCurrentDropdownOpen, isCurrentDropdownOpen } = navbarStore;
</script>

<template>
  <li>
    <PlusMenuLink :link="URL_SEARCH">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconMagnifyingGlass"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.search') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink
      :link="URL_USER_INBOX"
      :has-pin="notifications.unread_messages > 0"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconEnvelope"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.inbox') }}

        <template v-if="notifications.unread_messages > 0">
          ({{ notifications.unread_messages }})
        </template>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink
      :link="URL_USER_PROPOSAL"
      :has-pin="notifications.unread_match_proposal > 0"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconHeartStraight"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.selection_item') }}

        <template v-if="notifications.unread_match_proposal > 0">
          ({{ notifications.unread_match_proposal }})
        </template>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_DEALS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconAlignTop"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.deals') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.PROPERTY_HOLDINGS)"
      :submenus="[
        { name: trans('user_menu.offers'), link: URL_USER_OFFERS },
        { name: trans('user_menu.spaces'), link: URL_ADDRESS_PAGE },
      ]"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.PROPERTY_HOLDINGS : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBuildings"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.property_holdings') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
      :submenus="[
        { name: trans('user_menu.organization'), link: URL_USER_ORGANIZATION_MANAGE },
        { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
      ]"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBriefCase"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user.menu.my_organization') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_PROFILE_MANAGE">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.profile') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_NOTIFICATIONS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBellRinging"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.notifications') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink :link="URL_OFFER_CREATE">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconPlus"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.create_offer') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <Divider class="m-0" color="gray" />
  </li>
</template>
